import { CompanyTripDetailReportDtoPagingResponse, CompanyTripReportDto } from '@/__generated';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Col, Form, Input, Row, Select, Space, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
  ChartOptions,
} from 'chart.js';
import { exportService } from '@/services/exportService';
import { ExportExcelBtn, ExportPdfBtn } from '@/components/app/ExportBtn';
type Props = {};
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Title, Tooltip, Legend, ArcElement, BarElement);

const PAGE_TITLE = 'Un Earned Revenue Report';

export const barOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  scales: {
    x: {
      ticks: {
        // Include a dollar sign in the ticks
        callback: function(value, index, ticks) {
          return formatCurrency(value);
        },
      },
    },
  },

  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: v => {
          return `Total amount: ${formatCurrency(v.raw)}`;
        },
      },
      position: 'average',
      intersect: false,
      padding: 10,
      caretSize: 4,
      borderWidth: 2,
    },
  },
};
export const reportColors = ['#ff0000', '#ffe5a1', '#ffc0cb', '#008000', '#ffa500', '#00bfff', '#800080'];
const UnEarnedRevenueReport = (props: Props) => {
  const [searchForm] = Form.useForm();
  const [height, setHeight] = React.useState(500);
  const [report, setReport] = React.useState<any>({
    labels: [],
    datasets: [],
  });

  const [loading, setIsLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<{ fromDate: string; toDate: string }>({
    fromDate: formatApiDate(moment().add(-1, 'month')),
    toDate: formatApiDate(moment()),
  });

  React.useEffect(() => {
    setIsLoading(true);
    apiSdk.CompanyUnEarnedRevenueReportService.getReports({
      ...filter,
    })
      .then(res => {
        setHeight(Math.max(500, res.length * 25));
        let data = {
          labels: res.map(d => dateTimeDisplayFormat.dateOnly(d.date)),
          datasets: [
            {
              label: t('Ticket amount'),
              data: res.map(d => d.ticketAmount),
              backgroundColor: res.map(d => reportColors[moment(d.date).day()]),
              borderWidth: 0,
              borderRadius: 10,
              borderSkipped: false,
            },
          ],
        };
        setReport(data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const onSearch = ({ fromDate, toDate, ...props }: any) => {
    setFilter({
      ...props,
      fromDate: formatApiDate(fromDate),
      toDate: formatApiDate(toDate),
      pageIndex: 1,
    });
  };

  const onExportExcel = () => {
    searchForm.validateFields().then(res => {
      exportService.exportUnearnedReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toExcel();
    });
  };
  const onExportPdf = () => {
    searchForm.validateFields().then(res => {
      exportService.exportUnearnedReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toPdf();
    });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
          <ExportExcelBtn onClick={onExportExcel} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Form
          layout="inline"
          className="nta-custom-form"
          form={searchForm}
          colon={false}
          onFinish={onSearch}
          initialValues={filter}
          style={{ marginTop: 10 }}
        >
          <Row gutter={ROW_GUTTER}>
            <Col md={24} xl={24} xxl={24}>
              <Space>
                <Form.Item name="fromDate" label={t('From date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="toDate" label={t('To Date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>

        <Spin spinning={loading}>
          <div style={{ height: height }}>
            <Bar options={barOptions} data={report} />
          </div>
        </Spin>
      </div>
    </MainContent>
  );
};
export default UnEarnedRevenueReport;
